import { Button } from '@neo/index'
import { X } from 'lucide-react'
import type { RFileUploadResponse } from 'v2/types/api/auth'

interface MediaPreviewProps {
  files: RFileUploadResponse[]
  onRemove: (file: RFileUploadResponse) => void
}

function formatFileSize(bytes: number): string {
  if (!bytes) return '0 KB'
  const kb = bytes / 1024
  return `${Math.round(kb)}KB`
}

const PreviewThumbnail = ({ file }: { file: RFileUploadResponse }) => {
  const NodeVar = () => <div className="font-jetbrains text-p-500 text-secondary">{'{{ }}'}</div>
  const Document = () => (
    <div className="flex h-full w-full items-center justify-center bg-bg2">
      <span className="font-jetbrains text-xs-400 text-secondary break-all">{file.file_type}</span>
    </div>
  )
  const Image = () => (
    <img
      src={file.file_url}
      alt={file.file_name || 'Preview'}
      className="object-cover h-full w-full rounded"
    />
  )
  const isNodeVar = !file.file_name
  return (
    <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-md p-1 flex items-center justify-center">
      {isNodeVar && <NodeVar />}
      {!isNodeVar && file.file_type?.includes('image') && <Image />}
      {!isNodeVar && !file.file_type?.includes('image') && <Document />}
    </div>
  )
}

const FileDetails = ({ file }: { file: RFileUploadResponse }) => {
  const isNodeVar = !file.file_name
  return (
    <div className="flex-1 min-w-0">
      <p
        className={`text-xs-500 text-secondary truncate max-w-[250px] ${isNodeVar ? 'font-jetbrains' : 'font-inter'}`}
      >
        {file.file_name || file.file_url}
      </p>
      {!isNodeVar && (
        <p className="font-jetbrains text-xs-400 text-secondary tabular-nums">
          {formatFileSize(file.file_size || 0).toUpperCase()}
        </p>
      )}
    </div>
  )
}

export const MediaPreview = ({ files, onRemove }: MediaPreviewProps) => {
  if (files.length === 0) return null

  return (
    <div className="mt-4 space-y-4">
      <h2 className="text-h4-500">Media preview</h2>
      <div className="space-y-2">
        {files.map((file) => (
          <div
            key={file.file_id}
            className="flex items-center gap-4 rounded-lg bg-bg3 transition-colors hover:bg-hv1"
            data-testid="preview-media-item"
          >
            <div className="min-w-0 flex-1 flex items-center gap-4">
              <PreviewThumbnail file={file} />
              <FileDetails file={file} />
            </div>
            <Button
              variant="ghost"
              size="icon"
              type="button"
              onClick={() => onRemove(file)}
              className="ml-auto shrink-0 text-secondary hover:text-foreground"
              data-testid="button-remove-media"
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Remove file</span>
            </Button>
          </div>
        ))}
      </div>
    </div>
  )
}
